import { useEffect, useMemo, useState } from 'react';

import { detectFrontalFaceLandmarks } from './detectLandmarks';
import { buildFrontalFaceRegions } from './geometry';
import './FaceRegionMask.css';

/**
 * Frontal face-region SVG overlay for skin concern previews.
 * Matches object-fit: cover via preserveAspectRatio slice.
 */
export default function FaceRegionMask({
  imageUrl,
  active = true,
  showLabels = true,
  filled = true,
  visibleGroups = null,
}) {
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!active || !imageUrl) {
      setPayload(null);
      return undefined;
    }

    let cancelled = false;

    detectFrontalFaceLandmarks(imageUrl)
      .then((result) => {
        if (cancelled) return;
        setPayload(result);
      })
      .catch(() => {
        if (cancelled) return;
        setPayload(null);
      });

    return () => {
      cancelled = true;
    };
  }, [active, imageUrl]);

  const regions = useMemo(() => {
    if (!payload?.landmarks) return [];
    return buildFrontalFaceRegions(payload.landmarks, payload.width, payload.height);
  }, [payload]);

  const filtered = useMemo(() => {
    if (!visibleGroups || !visibleGroups.length) return regions;
    const allow = new Set(visibleGroups);
    return regions.filter((r) => allow.has(r.group));
  }, [regions, visibleGroups]);

  if (!active || !imageUrl || !payload || !filtered.length) {
    return null;
  }

  return (
    <svg
      className="face-region-mask-overlay"
      viewBox={`0 0 ${payload.width} ${payload.height}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      {filtered.map((region) => (
        <g key={region.id} data-group={region.group}>
          <path
            className={`face-region-mask-path${filled ? '' : ' face-region-mask-path--outline'}`}
            d={region.d}
            fill={filled ? region.color : 'none'}
          />
          {showLabels ? (
            <text
              className="face-region-mask-label"
              x={region.cx}
              y={region.cy}
            >
              {region.label}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}
